import CustomerSessionGuard from "./components/CustomerSessionGuard";
import CustomerAccessGate from '@/components/admin/CustomerAccessGate';
import React, { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import CustomerRegister from "./pages/CustomerRegister";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "@/context/AuthContext";
import BackToTop from "@/components/effects/BackToTop";

const DesktopEffects = lazy(
  () => import("@/components/effects/DesktopEffects")
);

// Pagini publice — încărcate numai când sunt accesate.
const Home = lazy(() => import("@/pages/Home"));
const AdminAccessChoice = lazy(() => import('./pages/AdminAccessChoice'));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const CustomerLogin = lazy(() => import("@/pages/CustomerLogin"));
const CustomerActivation = lazy(() => import("@/pages/CustomerActivation"));
const CustomerForgotPassword = lazy(() => import("@/pages/CustomerForgotPassword"));
const CustomerResetPassword = lazy(() => import("@/pages/CustomerResetPassword"));
const CustomerDashboard = lazy(() => import("@/pages/CustomerDashboard"));
const AgentLogin = lazy(() => import("@/pages/AgentLogin"));
const AgentActivation = lazy(() => import("@/pages/AgentActivation"));
const AgentDashboard = lazy(() => import("@/pages/AgentDashboard"));
const BeforeAfter = lazy(() => import("@/pages/BeforeAfter"));
const AboutKirby = lazy(() => import("@/pages/AboutKirby"));
const BlogList = lazy(() => import("@/pages/BlogList"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Subscriptions = lazy(() => import("@/pages/Subscriptions"));
const SubscriptionsPage = lazy(() => import("@/pages/SubscriptionsPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const ProductReturn = lazy(() => import("@/pages/ProductReturn"));
const ConsumablesMaintenancePage = lazy(
  () => import("@/pages/ConsumablesMaintenancePage")
);
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const WriteReviewPage = lazy(() => import("@/pages/WriteReviewPage"));
const TermsAndConditions = lazy(
  () => import("@/pages/TermsAndConditions")
);
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const DeliveryPolicy = lazy(() => import("@/pages/DeliveryPolicy"));
const CancellationPolicy = lazy(
  () => import("@/pages/CancellationPolicy")
);
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const SafeBuyGuide = lazy(() => import("@/pages/SafeBuyGuide"));

// Admin — nu mai intră în pachetul descărcat de vizitatorii site-ului.
const AdminLayout = lazy(
  () => import("@/components/admin/AdminLayout")
);
const CustomerAccessRequests = lazy(
  () => import("@/components/admin/CustomerAccessRequests")
);
const AdminDashboard = lazy(
  () => import("@/components/admin/AdminDashboard")
);
const ReviewsManagement = lazy(
  () => import("@/components/admin/ReviewsManagement")
);
const ClientsManagement = lazy(
  () => import("@/components/admin/ClientsManagement")
);
const CRMManagement = lazy(
  () => import("@/components/admin/CRMManagement")
);
const ReturnRequests = lazy(
  () => import("@/components/admin/ReturnRequests")
);
const SubscriptionInquiriesManagement = lazy(
  () => import("@/components/admin/SubscriptionInquiriesManagement")
);
const Settings = lazy(
  () => import("@/components/admin/Settings")
);
const ImagesManagement = lazy(
  () => import("@/components/admin/ImagesManagement")
);
const LogsView = lazy(
  () => import("@/components/admin/LogsView")
);
const UsersManagement = lazy(
  () => import("@/components/admin/UsersManagement")
);
const AgentAccountsManagement = lazy(
  () => import("@/components/admin/AgentAccountsManagement")
);
const ContactMessages = lazy(
  () => import("@/components/admin/ContactMessages")
);
const SocialMediaManagement = lazy(
  () => import("@/components/admin/SocialMediaManagement")
);
const CustomerAccountsManagement = lazy(
  () => import("@/components/admin/CustomerAccountsManagement")
);
const SubscriptionsManagement = lazy(
  () => import("@/components/admin/SubscriptionsManagement")
);
const TopSubscriptionsManagement = lazy(
  () => import("@/components/admin/TopSubscriptionsManagement")
);
const BlogManagement = lazy(
  () => import("@/components/admin/BlogManagement")
);
const TopAgenti = lazy(
  () => import("@/components/admin/TopAgenti")
);
const EmployeesManagement = lazy(
  () => import("@/components/admin/EmployeesManagement")
);
const ChangeRequests = lazy(
  () => import("@/components/admin/ChangeRequests")
);

function App() {
  const [desktopEffects, setDesktopEffects] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(
      "(min-width: 769px) and (prefers-reduced-motion: no-preference)"
    );

    const updateEffects = () => setDesktopEffects(query.matches);
    updateEffects();

    query.addEventListener?.("change", updateEffects);

    return () => {
      query.removeEventListener?.("change", updateEffects);
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#1f1f27',
              color: '#fff',
              border: '1px solid rgba(212,175,55,0.35)',
              borderRadius: '14px',
              padding: '14px 18px',
              boxShadow: '0 18px 50px rgba(0,0,0,0.35)'
            },
            success: {
              iconTheme: {
                primary: '#D4AF37',
                secondary: '#111'
              }
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#111'
              }
            }
          }}
        />
        <ScrollToTop />

        {desktopEffects && (
          <Suspense fallback={null}>
            <DesktopEffects />
          </Suspense>
        )}

        <BackToTop />

        <Suspense
          fallback={
            <div
              className="flex min-h-screen items-center justify-center bg-[#08090c] text-[#d4af37]"
              role="status"
              aria-live="polite"
            >
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#d4af37]" />
              <span className="sr-only">Se încarcă pagina</span>
            </div>
          }
        >
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/autentificare" element={<CustomerLogin />} />
          <Route path="/inregistrare" element={<CustomerRegister />} />
          <Route path="/activare-cont" element={<CustomerActivation />} />
          <Route path="/recuperare-parola" element={<CustomerForgotPassword />} />
          <Route path="/resetare-parola" element={<CustomerResetPassword />} />
          <Route path="/contul-meu" element={<CustomerSessionGuard><CustomerDashboard /></CustomerSessionGuard>} />

          {/* Portal separat pentru agenti */}
          <Route path="/agent" element={<Navigate to="/agent/login" replace />} />
          <Route path="/agent/login" element={<AgentLogin />} />
          <Route path="/agent/activate" element={<AgentActivation />} />
          <Route path="/agent/dashboard" element={<AgentDashboard />} />
          <Route path="/rezultate" element={<BeforeAfter />} />
          <Route path="/despre-kirby" element={<AboutKirby />} />
          <Route path="/abonamente-old" element={<Subscriptions />} />
          <Route path="/abonamente" element={<SubscriptionsPage />} />
          <Route path="/ghid-achizitie-kirby" element={<SafeBuyGuide />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/returnare-produs" element={<ProductReturn />} />
          <Route path="/consumabile-mentenanta" element={<ConsumablesMaintenancePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/recenzii" element={<ReviewsPage />} />
          <Route path="/scrie-recenzie" element={<WriteReviewPage />} />
          
          {/* Legal Pages */}
          <Route path="/termeni-si-conditii" element={<TermsAndConditions />} />
          <Route path="/politica-confidentialitate" element={<PrivacyPolicy />} />
          <Route path="/politica-livrare" element={<DeliveryPolicy />} />
          <Route path="/politica-anulare" element={<CancellationPolicy />} />
          
          {/* Location Pages - SEO Local */}
          <Route path="/locatii/:citySlug" element={<LocationPage />} />
          
          {/* Blog Pages */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          <Route path="/admin" element={<AdminAccessChoice />} />
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Protected Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="reviews" element={<ReviewsManagement />} />
            <Route path="clients" element={
              <CustomerAccessGate area="clients">
                <ClientsManagement />
              </CustomerAccessGate>
            } />
            <Route path="crm" element={
              <CustomerAccessGate area="clients">
                <CRMManagement />
              </CustomerAccessGate>
            } />
            <Route path="customer-accounts" element={
              <CustomerAccessGate area="customer_accounts">
                <CustomerAccountsManagement />
              </CustomerAccessGate>
            } />
            <Route path="returns" element={<ReturnRequests />} />
            <Route path="subscription-inquiries" element={<SubscriptionInquiriesManagement />} />
            <Route path="subscriptions" element={<SubscriptionsManagement />} />
            <Route path="subscription-performance" element={<TopSubscriptionsManagement />} />
            <Route path="contact-messages" element={<ContactMessages />} />
            <Route path="images" element={<ImagesManagement />} />
            <Route path="blog" element={<BlogManagement />} />
            <Route path="top-agenti" element={<TopAgenti />} />
            <Route path="employees" element={<EmployeesManagement />} />
            <Route path="change-requests" element={<ChangeRequests />} />
            <Route path="social-media" element={<SocialMediaManagement />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="agent-accounts" element={<AgentAccountsManagement />} />
            <Route path="customer-access-requests" element={<CustomerAccessRequests />} />
            <Route path="settings" element={<Settings />} />
            <Route path="logs" element={<LogsView />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
